import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, Spinner, KPICard, Badge } from '../ui';
import DirhamSymbol from '../components/DirhamSymbol';
import { useToastContext } from '../ui/feedback/ToastProvider';
import {
  TrendingUp,
  Calendar,
  Zap,
  BarChart3,
  Activity,
  RefreshCw,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const InvestorROI = () => {
  const { user } = useAuth();
  const { error: showError, success: showSuccess } = useToastContext();
  const [loading, setLoading] = useState(true);
  const [roiData, setROIData] = useState(null);
  const [roiStats, setROIStats] = useState(null);
  const [roiHistory, setROIHistory] = useState([]);
  const [timeframe, setTimeframe] = useState('30'); // days
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchROIData();
  }, [timeframe]);

  const fetchROIData = async () => {
    try {
      setLoading(true);
      const days = parseInt(timeframe);
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);

      const [latestRes, historyRes, statsRes] = await Promise.all([
        api.get('/api/roi/latest').catch(() => ({ data: { data: null } })),
        api.get(`/api/roi/history?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&limit=${days}`),
        api.get(`/api/roi/stats?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`)
      ]);

      setROIData(latestRes.data.data);
      setROIHistory(historyRes.data.data?.records || []);
      setROIStats(statsRes.data.data);
    } catch (error) {
      console.error('Error fetching ROI data:', error);
      showError('Failed to fetch ROI data');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchROIData();
    setRefreshing(false);
    showSuccess('ROI data refreshed');
  };

  // Prepare chart data
  const chartData = roiHistory.map(roi => ({
    date: new Date(roi.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    roiPercentage: (roi.roi_percentage || 0).toFixed(2),
    dailyReturn: (roi.daily_return || 0).toFixed(2),
    monthlyReturn: (roi.monthly_return || 0).toFixed(2),
  })).reverse();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-primary-900 dark:text-primary-50 flex items-center gap-2">
            <BarChart3 size={28} className="text-primary-600 dark:text-primary-400" />
            Your ROI Performance
          </h1>
          <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
            Track your daily ROI returns and performance
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg transition-colors"
        >
          <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Time Frame Selector */}
      <Card className="p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-primary-600 dark:text-primary-400">View:</span>
          {['7', '14', '30', '60', '90'].map(days => (
            <button
              key={days}
              onClick={() => setTimeframe(days)}
              className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                timeframe === days
                  ? 'bg-primary-600 text-white'
                  : 'bg-primary-100 dark:bg-primary-800 text-primary-900 dark:text-primary-50 hover:bg-primary-200 dark:hover:bg-primary-700'
              }`}
            >
              {days} days
            </button>
          ))}
        </div>
      </Card>

      {/* KPI Cards */}
      {roiStats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 lg:gap-6">
          <KPICard
            title="Total Daily Return"
            value={
              <span className="flex items-center gap-1">
                <DirhamSymbol size={24} inline />
                {(roiStats.totalDailyReturn || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            }
            icon={<TrendingUp size={24} />}
            color="success"
            subtitle={`${roiStats.dayCount} days`}
          />
          <KPICard
            title="Average ROI %"
            value={`${(roiStats.avgROIPercentage || 0).toFixed(2)}%`}
            icon={<Zap size={24} />}
            color="info"
            subtitle={`${roiStats.minROIPercentage.toFixed(2)}% - ${roiStats.maxROIPercentage.toFixed(2)}%`}
          />
          <KPICard
            title="Est. Monthly Return"
            value={
              <span className="flex items-center gap-1">
                <DirhamSymbol size={24} inline />
                {(roiStats.estimatedMonthlyReturn || 0).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
            }
            icon={<Activity size={24} />}
            color="warning"
          />
          {roiData && (
            <KPICard
              title="Today's ROI %"
              value={`${(roiData.roi_percentage || 0).toFixed(2)}%`}
              icon={<Calendar size={24} />}
              color="primary"
              subtitle={
                <span className="flex items-center gap-1">
                  Daily: <DirhamSymbol size={12} inline />
                  {(roiData.daily_return || 0).toFixed(2)}
                </span>
              }
            />
          )}
        </div>
      )}

      {/* Charts */}
      {chartData.length > 0 && (
        <>
          {/* ROI Percentage Chart */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-6 flex items-center gap-2">
              <BarChart3 size={20} className="text-primary-600 dark:text-primary-400" />
              ROI Percentage Trend
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'ROI %', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                  formatter={(value) => `${value}%`}
                />
                <Line
                  type="monotone"
                  dataKey="roiPercentage"
                  stroke="#0A7E66"
                  strokeWidth={2}
                  dot={{ fill: '#0A7E66', r: 4 }}
                  activeDot={{ r: 6 }}
                  name="ROI %"
                />
              </LineChart>
            </ResponsiveContainer>
          </Card>

          {/* Daily Return Chart */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-6 flex items-center gap-2">
              <TrendingUp size={20} className="text-success-600 dark:text-success-400" />
              Daily Return Trend
            </h2>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorDailyReturn" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0.1} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis
                  dataKey="date"
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  stroke="#666"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Return ($)', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                  }}
                  formatter={(value) => `$${value}`}
                />
                <Area
                  type="monotone"
                  dataKey="dailyReturn"
                  stroke="#10B981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorDailyReturn)"
                  name="Daily Return"
                />
              </AreaChart>
            </ResponsiveContainer>
          </Card>

          {/* ROI History Table */}
          <Card className="p-6">
            <h2 className="text-lg font-semibold text-primary-900 dark:text-primary-50 mb-6 flex items-center gap-2">
              <Activity size={20} className="text-primary-600 dark:text-primary-400" />
              ROI History
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-primary-200 dark:border-primary-700">
                    <th className="text-left px-4 py-3 font-semibold text-primary-900 dark:text-primary-50 text-sm">
                      Date
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-primary-900 dark:text-primary-50 text-sm">
                      ROI %
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-primary-900 dark:text-primary-50 text-sm">
                      Daily Return
                    </th>
                    <th className="text-left px-4 py-3 font-semibold text-primary-900 dark:text-primary-50 text-sm">
                      Monthly Est.
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {roiHistory.map((roi, index) => (
                    <tr key={index} className="border-b border-primary-100 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors">
                      <td className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50">
                        <div className="flex items-center gap-2">
                          <Calendar size={14} className="text-primary-600 dark:text-primary-400" />
                          {new Date(roi.date).toISOString().split('T')[0]}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-primary-900 dark:text-primary-50">
                        <div className="flex items-center gap-1">
                          <Zap size={14} className="text-yellow-500" />
                          {(roi.roi_percentage || 0).toFixed(2)}%
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-success-600 dark:text-success-400">
                        <div className="flex items-center gap-1">
                          <DirhamSymbol size={14} inline />
                          {(roi.daily_return || 0).toFixed(2)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-primary-900 dark:text-primary-50">
                        <div className="flex items-center gap-1">
                          <DirhamSymbol size={14} inline />
                          {(roi.monthly_return || 0).toFixed(2)}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* No Data */}
      {chartData.length === 0 && !loading && (
        <Card className="p-12 text-center">
          <BarChart3 size={48} className="mx-auto text-primary-300 dark:text-primary-600 mb-4" />
          <p className="text-primary-600 dark:text-primary-400">
            No ROI data available for the selected period
          </p>
        </Card>
      )}
    </div>
  );
};

export default InvestorROI;
